/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Coins, Download, Eye, MoreVertical,
  CheckCircle, XCircle, Clock, RefreshCw,
  DollarSign, TrendingUp, Calendar, Ban, RotateCcw,
  Plus, X, Briefcase
} from 'lucide-react';
import { useCommissions, useCommissionStats, useApproveCommission, useCancelCommission, useCreateCommission } from '@/hooks/api/useCommissions';
import { usePartners } from '@/hooks/api/usePartners';
import { Commission, CommissionStatus, CommissionType, CreateCommissionRequest } from '@/types/superadmin.types';
import { SearchableSelect, SelectOption } from '@/components/ui/SearchableSelect';
import { SearchInput } from '@/components/ui';

// Status Badge
const StatusBadge: React.FC<{ status: CommissionStatus; t: (key: string) => string }> = ({ status, t }) => {
  const config: Record<CommissionStatus, { bg: string; text: string; icon: typeof CheckCircle; labelKey: string }> = {
    PENDING: { bg: 'bg-amber-500/15', text: 'text-amber-500', icon: Clock, labelKey: 'commissions.status.pending' },
    APPROVED: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: CheckCircle, labelKey: 'commissions.status.approved' },
    PAID: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle, labelKey: 'commissions.status.paid' },
    CANCELLED: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle, labelKey: 'commissions.status.cancelled' },
    REVERSED: { bg: 'bg-red-500/15', text: 'text-red-500', icon: RotateCcw, labelKey: 'commissions.status.reversed' },
  };
  const { bg, text, icon: Icon, labelKey } = config[status] || config.PENDING;

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(labelKey)}
    </span>
  );
};

// Type Badge
const TypeBadge: React.FC<{ type: CommissionType; t: (key: string) => string }> = ({ type, t }) => {
  const config: Record<CommissionType, { bg: string; text: string }> = {
    SUBSCRIPTION: { bg: 'bg-blue-500/15', text: 'text-blue-500' },
    BONUS: { bg: 'bg-purple-500/15', text: 'text-purple-500' },
    ADJUSTMENT: { bg: 'bg-orange-500/15', text: 'text-orange-500' },
    REFERRAL_BONUS: { bg: 'bg-green-500/15', text: 'text-green-500' },
    MANUAL: { bg: 'bg-gray-500/15', text: 'text-gray-400' },
  };
  const { bg, text } = config[type] || config.SUBSCRIPTION;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${bg} ${text}`}>
      {t(`commissions.type.${type.toLowerCase()}`)}
    </span>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="commissions-table-row grid grid-cols-[1.5fr_1fr_100px_80px_100px_120px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle animate-pulse">
    <div>
      <div className="h-4 w-32 bg-muted/20 rounded mb-2"></div>
      <div className="h-3 w-24 bg-muted/20 rounded"></div>
    </div>
    <div className="h-4 w-24 bg-muted/20 rounded"></div>
    <div className="h-5 w-16 bg-muted/20 rounded"></div>
    <div className="h-5 w-12 bg-muted/20 rounded"></div>
    <div className="h-5 w-16 bg-muted/20 rounded-full"></div>
    <div className="h-4 w-20 bg-muted/20 rounded"></div>
    <div className="w-7 h-7 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="w-10 h-10 rounded-lg bg-muted/20"></div>
    </div>
    <div className="h-6 w-20 bg-muted/20 rounded mb-1"></div>
    <div className="h-3 w-28 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  color: 'green' | 'blue' | 'red' | 'amber' | 'purple';
}> = ({ icon, value, label, color }) => {
  const colors = {
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    red: 'bg-red-500/15 text-red-500',
    amber: 'bg-amber-500/15 text-amber-500',
    purple: 'bg-purple-500/15 text-purple-500',
  };

  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
      </div>
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
};

// Action Menu
const ActionMenu: React.FC<{
  commission: Commission;
  onApprove: (id: string) => void;
  onCancel: (id: string) => void;
  isApproving: boolean;
  isCancelling: boolean;
  t: (key: string) => string;
}> = ({ commission, onApprove, onCancel, isApproving, isCancelling, t }) => {
  const [isOpen, setIsOpen] = useState(false);

  const canApprove = commission.status === 'PENDING';
  const canCancel = commission.status === 'PENDING' || commission.status === 'APPROVED';

  if (!canApprove && !canCancel) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-8 z-20 bg-surface border border-border-subtle rounded-lg shadow-lg py-1 min-w-[140px]">
            {canApprove && (
              <button
                onClick={() => {
                  onApprove(commission.id);
                  setIsOpen(false);
                }}
                disabled={isApproving}
                className="w-full px-3 py-2 text-left text-sm hover:bg-hover flex items-center gap-2 text-emerald-500"
              >
                <CheckCircle className="w-4 h-4" />
                {t('commissions.actions.approve')}
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => {
                  onCancel(commission.id);
                  setIsOpen(false);
                }}
                disabled={isCancelling}
                className="w-full px-3 py-2 text-left text-sm hover:bg-hover flex items-center gap-2 text-red-500"
              >
                <Ban className="w-4 h-4" />
                {t('commissions.actions.cancel')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Create Commission Modal
const CreateCommissionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ isOpen, onClose, onSuccess, t }) => {
  const [formData, setFormData] = useState({
    partnerId: '',
    commissionType: 'BONUS' as CommissionType,
    amount: '',
    baseAmount: '',
    commissionRate: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch partners for dropdown
  const { data: partnersData, isLoading: isLoadingPartners } = usePartners({ size: 100, status: 'ACTIVE' });
  const partners = partnersData?.content || [];

  // Create mutation
  const createMutation = useCreateCommission();

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Auto-calculate commission rate when amount and baseAmount change
    if (field === 'amount' || field === 'baseAmount') {
      const newFormData = { ...formData, [field]: value };
      const amount = parseFloat(newFormData.amount);
      const baseAmount = parseFloat(newFormData.baseAmount);
      if (!isNaN(amount) && !isNaN(baseAmount) && baseAmount > 0) {
        const rate = ((amount / baseAmount) * 100).toFixed(2);
        setFormData(prev => ({ ...prev, [field]: value, commissionRate: rate }));
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.partnerId) {
      newErrors.partnerId = t('commissions.modal.errors.partnerRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('commissions.modal.errors.amountRequired');
    }
    if (!formData.baseAmount || parseFloat(formData.baseAmount) <= 0) {
      newErrors.baseAmount = t('commissions.modal.errors.baseAmountRequired');
    }
    if (!formData.commissionRate || parseFloat(formData.commissionRate) < 0) {
      newErrors.commissionRate = t('commissions.modal.errors.rateRequired');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('commissions.modal.errors.reasonRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const request: CreateCommissionRequest = {
      partnerId: formData.partnerId,
      commissionType: formData.commissionType,
      amount: parseFloat(formData.amount),
      baseAmount: parseFloat(formData.baseAmount),
      commissionRate: parseFloat(formData.commissionRate),
      description: formData.description.trim(),
    };

    createMutation.mutate(request, {
      onSuccess: () => {
        // Reset form
        setFormData({
          partnerId: '',
          commissionType: 'BONUS',
          amount: '',
          baseAmount: '',
          commissionRate: '',
          description: '',
        });
        setErrors({});
        onSuccess();
        onClose();
      },
    });
  };

  const handleClose = () => {
    setFormData({
      partnerId: '',
      commissionType: 'BONUS',
      amount: '',
      baseAmount: '',
      commissionRate: '',
      description: '',
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-surface border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {t('commissions.modal.title')}
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Partner Selection */}
          <SearchableSelect
            label={t('commissions.modal.partner')}
            options={partners.map((partner): SelectOption => ({
              value: partner.id,
              label: partner.name,
              description: partner.company,
              icon: <Briefcase className="w-4 h-4 text-purple-500" />,
            }))}
            value={formData.partnerId}
            onChange={(value) => handleChange('partnerId', value)}
            placeholder={t('commissions.modal.selectPartner')}
            searchPlaceholder={t('commissions.modal.searchPartner', { defaultValue: 'Search partners...' })}
            noResultsText={t('commissions.modal.noPartnersFound', { defaultValue: 'No partners found' })}
            isLoading={isLoadingPartners}
            error={errors.partnerId}
            required
            clearable
          />

          {/* Commission Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('commissions.modal.type')} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.commissionType}
              onChange={(e) => handleChange('commissionType', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="BONUS">{t('commissions.type.bonus')}</option>
              <option value="ADJUSTMENT">{t('commissions.type.adjustment')}</option>
              <option value="REFERRAL_BONUS">{t('commissions.type.referral_bonus')}</option>
              <option value="MANUAL">{t('commissions.type.manual')}</option>
            </select>
          </div>

          {/* Amount and Base Amount Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('commissions.modal.amount')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-7 pr-3 py-2 bg-background border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    errors.amount ? 'border-red-500' : 'border-border'
                  }`}
                />
              </div>
              {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('commissions.modal.baseAmount')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.baseAmount}
                  onChange={(e) => handleChange('baseAmount', e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-7 pr-3 py-2 bg-background border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    errors.baseAmount ? 'border-red-500' : 'border-border'
                  }`}
                />
              </div>
              {errors.baseAmount && <p className="text-xs text-red-500 mt-1">{errors.baseAmount}</p>}
            </div>
          </div>

          {/* Commission Rate */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('commissions.modal.rate')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={formData.commissionRate}
                onChange={(e) => handleChange('commissionRate', e.target.value)}
                placeholder="0.00"
                className={`w-full pr-8 pl-3 py-2 bg-background border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  errors.commissionRate ? 'border-red-500' : 'border-border'
                }`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted">%</span>
            </div>
            {errors.commissionRate && <p className="text-xs text-red-500 mt-1">{errors.commissionRate}</p>}
            <p className="text-xs text-muted mt-1">{t('commissions.modal.rateHint')}</p>
          </div>

          {/* Reason/Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('commissions.modal.reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('commissions.modal.reasonPlaceholder')}
              rows={3}
              className={`w-full px-3 py-2 bg-background border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none ${
                errors.description ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>

          {/* Error message from API */}
          {createMutation.isError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-500">
              {createMutation.error?.message || t('commissions.modal.errors.createFailed')}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-foreground hover:bg-hover rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {createMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('commissions.modal.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Commissions Page
export default function CommissionsPage() {
  const { t } = useTranslation('platform');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch commissions with filters
  const { data, isLoading, isFetching, refetch } = useCommissions({
    page: currentPage,
    size: 20,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
  });

  // Fetch stats
  const { data: stats, isLoading: isLoadingStats } = useCommissionStats();

  // Mutations
  const approveMutation = useApproveCommission();
  const cancelMutation = useCancelCommission();

  const commissions = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;

  // Filter by search query (client-side)
  const filteredCommissions = useMemo(() => {
    if (!searchQuery) return commissions;
    const query = searchQuery.toLowerCase();
    return commissions.filter(
      (c) =>
        c.partnerName?.toLowerCase().includes(query) ||
        c.organizationName?.toLowerCase().includes(query) ||
        c.invoiceNumber?.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
    );
  }, [commissions, searchQuery]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleApprove = (id: string) => {
    approveMutation.mutate(id, {
      onSuccess: () => refetch(),
    });
  };

  const handleCancel = (id: string) => {
    cancelMutation.mutate({ id, reason: 'Cancelled by admin' }, {
      onSuccess: () => refetch(),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Coins className="w-6 h-6" /> {t('commissions.title')}
          </h1>
          <p className="text-sm text-muted">{t('commissions.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-border-subtle rounded-lg text-sm font-medium text-foreground hover:bg-hover">
            <Download className="w-4 h-4" /> {t('common.export')}
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" /> {t('commissions.addCommission')}
          </button>
        </div>
      </div>

      {/* Create Commission Modal */}
      <CreateCommissionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => refetch()}
        t={t}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {isLoadingStats ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<DollarSign className="w-5 h-5" />}
              value={formatCurrency(stats?.totalPaid || 0)}
              label={t('commissions.stats.totalPaid')}
              color="green"
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              value={formatCurrency(stats?.totalPending || 0)}
              label={t('commissions.stats.totalPending')}
              color="amber"
            />
            <StatCard
              icon={<TrendingUp className="w-5 h-5" />}
              value={(stats?.pendingCount || 0).toString()}
              label={t('commissions.stats.pendingCount')}
              color="blue"
            />
            <StatCard
              icon={<Calendar className="w-5 h-5" />}
              value={(stats?.totalCommissions || 0).toString()}
              label={t('commissions.stats.totalCommissions')}
              color="purple"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('commissions.searchPlaceholder')}
          className="flex-1 max-w-xs"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as CommissionStatus | 'ALL');
            setCurrentPage(0);
          }}
          className="px-3 py-2 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
        >
          <option value="ALL">{t('commissions.filters.allStatus')}</option>
          <option value="PENDING">{t('commissions.status.pending')}</option>
          <option value="APPROVED">{t('commissions.status.approved')}</option>
          <option value="PAID">{t('commissions.status.paid')}</option>
          <option value="CANCELLED">{t('commissions.status.cancelled')}</option>
          <option value="REVERSED">{t('commissions.status.reversed')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="commissions-table-wrap bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <div className="commissions-table-header grid grid-cols-[1.5fr_1fr_100px_80px_100px_120px_80px] gap-4 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('commissions.table.partner')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-org">{t('commissions.table.organization')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-amount">{t('commissions.table.amount')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-rate">{t('commissions.table.rate')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('commissions.table.status')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-date">{t('commissions.table.date')}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">{t('commissions.table.actions')}</div>
        </div>

        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : filteredCommissions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <Coins className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('commissions.noCommissions')}</p>
          </div>
        ) : (
          filteredCommissions.map((commission) => (
            <div
              key={commission.id}
              className="commissions-table-row grid grid-cols-[1.5fr_1fr_100px_80px_100px_120px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover"
            >
              <div>
                <div className="font-medium text-foreground text-sm">{commission.partnerName || 'Unknown Partner'}</div>
                <div className="text-xs text-muted mt-0.5">
                  <TypeBadge type={commission.commissionType} t={t} />
                </div>
              </div>
              <div className="col-org">
                <div className="text-sm text-foreground truncate">{commission.organizationName || '-'}</div>
                {commission.invoiceNumber && (
                  <div className="text-xs text-muted truncate">{commission.invoiceNumber}</div>
                )}
              </div>
              <div className="font-mono text-sm font-semibold text-foreground col-amount">
                {formatCurrency(commission.amount, commission.currency)}
              </div>
              <div className="text-sm text-secondary col-rate">
                {commission.commissionRate}%
              </div>
              <div><StatusBadge status={commission.status} t={t} /></div>
              <div className="text-sm text-muted col-date">{formatDate(commission.createdAt)}</div>
              <div>
                <ActionMenu
                  commission={commission}
                  onApprove={handleApprove}
                  onCancel={handleCancel}
                  isApproving={approveMutation.isPending}
                  isCancelling={cancelMutation.isPending}
                  t={t}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination info */}
      {!isLoading && totalElements > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted">
            {t('commissions.showing', { count: filteredCommissions.length, total: totalElements })}
          </div>
          {data?.page && data.page.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="px-3 py-1 text-sm border border-border-subtle rounded hover:bg-hover disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-muted">
                {currentPage + 1} / {data.page.totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage >= data.page.totalPages - 1}
                className="px-3 py-1 text-sm border border-border-subtle rounded hover:bg-hover disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .commissions-table-header,
          .commissions-table-row {
            grid-template-columns: 1fr 80px 80px 60px !important;
          }
          .col-org,
          .col-rate,
          .col-date {
            display: none !important;
          }
        }
        @media (max-width: 640px) {
          .commissions-table-wrap {
            overflow-x: auto;
          }
          .commissions-table-header,
          .commissions-table-row {
            min-width: 480px;
          }
        }
        @media (max-width: 480px) {
          .commissions-table-header,
          .commissions-table-row {
            grid-template-columns: 1fr 60px 60px !important;
          }
          .col-amount {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
